// Shared pieces for the admin Courses pages (Courses Management, Add New Content):
// admin-managed categories and subcategories, the category sidebar and small helpers.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { ChevronDown, ChevronRight, FolderPlus, Plus } from "lucide-react";
import Swal from "sweetalert2";
import { api } from "../../api";
import { resultDialog } from "../../utils/swalDialogs";
import { getSessionColumnHeader } from "../../utils/syllabusTopics";
import {
  LEARNING_ZONE_CATEGORIES,
  buildSubcategoryNodes,
  categoryLabel,
  withLearningZoneLook,
} from "../../utils/courseSubcategoryNodes";
import { emojiFieldHtml, wireEmojiField } from "./emojiField";
import { byOrder, coins as wholeCoins } from "./courseViewShared";
import "./coursesAdmin.css";

export const DIFFICULTY_TONE = { Beginner: "green", Intermediate: "amber", Advanced: "red" };

// Course counts per item type: modules / chapters / content items.
export const courseCounts = (course) => {
  const modules = course?.modules || [];
  const chapters = modules.reduce((n, m) => n + (m.chapters || []).length, 0);
  const items = modules.reduce(
    (n, m) => n + (m.chapters || []).reduce((k, ch) => k + (ch.contentItems || []).length, 0),
    0
  );
  return {
    modules: course?.moduleCount ?? modules.length,
    chapters: course?.chapterCount ?? chapters,
    items: course?.contentItemCount ?? items,
  };
};

// What admins see for a category: the student Learning Zone's name ("Art" -> "Fun with Art"), or its own.
export { categoryLabel };

// Loads the admin-managed categories (created on first use with the default set), named, ordered and
// iconed like the student Learning Zone sidebar (withLearningZoneLook: `label` is the shown name).
export function useCourseCategories() {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    try {
      const res = await api.get("/api/v2/lms/admin/courses/categories");
      setCategories(withLearningZoneLook(res.data?.data || []));
    } catch {
      setCategories([]);
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);
  return { categories, loading, reload };
}

const lower = (value) => String(value || "").trim().toLowerCase();
const ADDED_ID = /^[a-f0-9]{24}$/i;

/**
 * A category's subcategories, the same rows the student Learning Zone shows under it: its built-in
 * rows (English > Action Songs > Vocal Songs, Scenes ...), the task type rows of its published
 * courses (English > Poem) and the ones admins added. Each node is
 * { key, label, icon, kind: "builtIn" | "taskType" | "added", taskType, syllabusTopic, children };
 * `syllabusTopic` is the coach Syllabus Tracker topic the row belongs to (from its own row or the
 * nearest row above it, e.g. Action Songs > Vocal Songs > Hindi -> "Action Songs"), or "".
 * A course sits in a node through `course.subcategory` (= node.key), or, for a task type row,
 * through its task type.
 */
export function useCourseSubcategories() {
  const [rows, setRows] = useState({ subcategories: [], taskTypes: [], hiddenRows: [] });
  const reload = useCallback(async () => {
    try {
      const res = await api.get("/api/v2/lms/admin/courses/subcategories");
      setRows({ subcategories: res.data?.data || [], taskTypes: res.data?.taskTypes || [], hiddenRows: res.data?.hiddenRows || [] });
    } catch {
      setRows({ subcategories: [], taskTypes: [], hiddenRows: [] });
    }
  }, []);
  useEffect(() => { reload(); }, [reload]);

  // Built in utils/courseSubcategoryNodes.js, which the coach Syllabus Tracker's pickers share.
  const nodesFor = useMemo(() => buildSubcategoryNodes(rows).nodesFor, [rows]);

  return { nodesFor, reload };
}

export const findSubcategoryNode = (nodes, key) => {
  if (!key) return null;
  for (const node of nodes) {
    if (node.key === key) return node;
    const child = findSubcategoryNode(node.children, key);
    if (child) return child;
  }
  return null;
};

const nodeKeys = (node) => [node.key, ...node.children.flatMap(nodeKeys)];

// Whether a course is in a subcategory node (or one of its children).
export const courseInSubcategory = (course, node) => {
  if (node.kind === "taskType") {
    // Like the Learning Zone: a course placed in an added subcategory is listed there instead.
    return course.taskType === node.taskType && !ADDED_ID.test(course.subcategory || "");
  }
  return nodeKeys(node).includes(course.subcategory || "");
};

// Rows below a category at most (English > Scenes > Beginners > Week 1); the backend checks it too.
export const MAX_SUBCATEGORY_DEPTH = 3;

// The nodes from the top down to `key` ([] when it isn't there).
export const subcategoryChain = (nodes, key) => {
  for (const node of nodes) {
    if (node.key === key) return [node];
    const rest = subcategoryChain(node.children, key);
    if (rest.length) return [node, ...rest];
  }
  return [];
};

// "Action Songs › Vocal Songs" for a node key.
export const subcategoryPath = (nodes, key) => subcategoryChain(nodes, key).map((node) => node.label).join(" › ");

// Where a course sits under its category: the nodes from the top down to its subcategory, or, for a
// course only listed in a task type row (English > Poem), down to that row. [] = right in the category.
export const courseSubcategoryChain = (nodes, course) => {
  const chain = subcategoryChain(nodes, course?.subcategory || "");
  if (chain.length || !course?.taskType || ADDED_ID.test(course.subcategory || "")) return chain;
  const toTaskType = (list) => {
    for (const node of list) {
      if (node.kind === "taskType" && node.taskType === course.taskType) return [node];
      const rest = toTaskType(node.children);
      if (rest.length) return [node, ...rest];
    }
    return [];
  };
  return toTaskType(nodes);
};

// Rows a new subcategory can go under (leaving it within MAX_SUBCATEGORY_DEPTH), with their depth.
const parentChoices = (nodes, depth = 1) =>
  depth >= MAX_SUBCATEGORY_DEPTH ? [] : nodes.flatMap((node) => [{ node, depth }, ...parentChoices(node.children, depth + 1)]);

// "Action Songs" -> "Action Song", "Stories" -> "Story", "Rhymes" -> "Rhyme".
const singular = (label) => label.trim().replace(/ies$/i, "y").replace(/([^s])s$/i, "$1");
const escapeRegExp = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

// The category itself, as the row a course without a subcategory sits in, when it's one admins added
// (not a Learning Zone group such as English, whose courses without one keep untitled items).
const addedCategoryNode = (course) => {
  const name = String(course.category || "").trim();
  if (course.subcategory || !name) return null;
  if (LEARNING_ZONE_CATEGORIES.some((category) => category.name.toLowerCase() === name.toLowerCase())) return null;
  return { key: "", label: name, syllabusTopic: "" };
};

// Whether two courses sit in the same row: the same subcategory, or, without one, the same task type.
const sameRow = (a, b) =>
  a.category === b.category &&
  (a.subcategory || "") === (b.subcategory || "") &&
  Boolean(a.subcategory || (a.taskType || "") === (b.taskType || ""));

// The courses holding a row's content: `course` and the others in its row (not archived), oldest
// first (the list comes newest first). Admins add content to the row, not to a course.
export const coursesInRow = (courses, course) => {
  if (!course) return [];
  const inRow = courses.filter((c) => c.status !== "archived" && sameRow(c, course)).reverse();
  return inRow.some((c) => c._id === course._id) ? inRow : [...inRow, course];
};

// A content's coins, as Add New Content totals them: its default coins + its items' + its coin rules'.
export const contentCoins = (chapter) =>
  wholeCoins(chapter?.defaultCoins) +
  (chapter?.contentItems || []).reduce((total, item) => total + wholeCoins(item.coins), 0) +
  (chapter?.coinRules || []).reduce((total, rule) => total + wholeCoins(rule.coins), 0);

// Admins see content (a song, scene or story: a chapter with its PDF, audio, video, game and quiz),
// not the course it is saved in: every chapter of `courses`, in their order, as { key, course, module, chapter }.
export const contentRowsOf = (courses) =>
  courses.flatMap((course) =>
    byOrder(course.modules).flatMap((module) =>
      byOrder(module.chapters).map((chapter) => ({ key: String(chapter._id), course, module, chapter }))
    )
  );

/**
 * How Add New Content names a row's content items: "<prefix> <n>", named after the course's
 * subcategory ("Action Song", "Rhymes" -> "Rhyme") and numbered within that subcategory (all the
 * courses in it, coursesInRow), so every subcategory starts at "<prefix> 1" (Vocal and Instrumental
 * Songs each have their own Action Song 1, 2 ...). `first` = one past the row's items so far and past
 * the highest "<prefix> <n>" among their titles, so a deleted item's name isn't given twice.
 * `next` is the Syllabus Tracker number the items are saved with (not shown): numbered on from the
 * highest so far across the subcategory's tracker topic (Vocal and Instrumental Songs share the
 * Action Songs numbers), or across the subcategory's courses when it has no topic, so two courses'
 * "Action Song 1" stay two tracker columns with their own marks.
 * A course placed right in a category admins added (no subcategory; students see it as that
 * category's own Learning Zone row) is named after the category instead ("testing 1"), numbered
 * across that category's courses without a subcategory.
 * Returns { prefix, topic, next, first }, or null for a course without a known subcategory
 * (nor one right in an added category).
 */
export function contentItemNaming(categoryNodes, courses, course) {
  const node = course ? subcategoryChain(categoryNodes, course.subcategory || "").pop() || addedCategoryNode(course) : null;
  if (!node) return null;
  const topic = node.syllabusTopic || "";
  const prefix = topic ? getSessionColumnHeader(topic) : singular(node.label);
  let highest = 0;
  let unnumbered = 0;
  courses
    .filter((c) => c.status !== "archived")
    .forEach((c) =>
      (c.modules || []).forEach((module) =>
        (module.chapters || []).forEach((chapter) => {
          const inScope = topic
            ? chapter.syllabusTopic === topic
            : c.category === course.category && (c.subcategory || "") === node.key;
          if (!inScope) return;
          if (chapter.sessionNumber) highest = Math.max(highest, chapter.sessionNumber);
          else unnumbered += 1;
        })
      )
    );
  const own = coursesInRow(courses, course).flatMap((c) => (c.modules || []).flatMap((module) => module.chapters || []));
  const named = new RegExp(`^${escapeRegExp(prefix)}\\s+(\\d+)$`, "i");
  const highestNamed = Math.max(0, ...own.map((chapter) => Number(named.exec(String(chapter.title || "").trim())?.[1]) || 0));
  return {
    prefix,
    topic,
    next: Math.max(highest, unnumbered) + 1,
    first: Math.max(own.length, highestNamed) + 1,
  };
}

// A last subcategory: one with no subcategories under it. Picking one in the sidebar opens Add New
// Content for it (addContentPath) instead of its card in Courses Management.
export const isLastRow = (node) => Boolean(node) && !node.children.length;
export const addContentPath = (category, sub) => `/admin/courses/content/new?${new URLSearchParams({ category, sub })}`;

// Computer Apps › GCompris (the built-in row) has the GCompris coin settings.
export const isGComprisRow = (category, node) =>
  lower(category) === "computer apps" && node?.kind === "builtIn" && node.key === "gcompris";

// The course Add New Content makes on Save for a last row (a last subcategory, or a category without
// subcategories, `node` = null) that has no course yet: named after the row and placed in it, or for
// a task type row (English > Poem) given its task type. Admins never pick it (`rowCourse`): it is
// published once its content is saved, without a thumbnail.
export const courseForRow = (category, node, description) => ({
  title: node ? node.label : categoryLabel(category),
  description,
  category,
  difficultyLevel: "Beginner",
  subcategory: node && node.kind !== "taskType" ? node.key : "",
  taskType: node?.kind === "taskType" ? node.taskType : "",
  icon: node?.icon || "📚",
  rowCourse: true,
});

export const categoryMeta = (categories, name) =>
  categories.find((c) => c.name === name) || { name: name || "Uncategorised", label: categoryLabel(name) || "Uncategorised", icon: "📚", color: "#64748b" };

// Badge colours derived from the category's accent colour.
export const CategoryBadge = ({ categories, name }) => {
  const meta = categoryMeta(categories, name);
  return (
    <span className="cm-badge" style={{ color: meta.color, background: `${meta.color}14`, borderColor: `${meta.color}33` }}>
      {meta.label || meta.name}
    </span>
  );
};

// An image can stand in for a category's emoji ("Upload image" in Add Category): uploaded to the
// storage bucket with the category, which keeps its link as `iconUrl`.
const ICON_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp", "image/gif"];
const MAX_ICON_IMAGE_BYTES = 1024 * 1024;

const readAsDataUrl = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });

// The uploaded image (a signed link, valid for an hour), or the emoji when it can't be shown.
function CategoryIconImage({ category }) {
  const [failed, setFailed] = useState(false);
  if (failed) return category.icon || "📚";
  return <img className="cm-cat-img" src={category.iconUrl} alt="" onError={() => setFailed(true)} />;
}

// A category's icon: its uploaded image (`iconUrl`), or else its emoji.
export const categoryIcon = (category) =>
  category?.iconUrl ? <CategoryIconImage key={category.iconUrl} category={category} /> : category?.icon;

// `categories` (the shown ones, useCourseCategories): a deleted student Learning Zone category added
// again by the name admins see ("Fun with Art") comes back as itself ("Art").
export async function promptAddCategory(categories) {
  // The picked image, as a data: URI ("" = none, the emoji is used).
  let iconImage = "";
  const { value } = await Swal.fire({
    title: "Add Category",
    html: `
      <div class="cm-swal-form">
        <label>Category name<input id="cm-cat-name" class="swal2-input" maxlength="50" placeholder="e.g. Music" /></label>
        ${emojiFieldHtml("cm-cat-icon")}
        <div class="cm-swal-upload">
          <span class="cm-swal-upload__or">or</span>
          <button type="button" id="cm-cat-icon-upload" class="cm-swal-upload__btn">Upload image</button>
          <img id="cm-cat-icon-preview" class="cm-swal-upload__preview" alt="Icon image preview" hidden />
          <button type="button" id="cm-cat-icon-remove" class="cm-swal-upload__remove" hidden>Remove</button>
          <input id="cm-cat-icon-file" type="file" accept="${ICON_IMAGE_TYPES.join(",")}" aria-label="Icon image" hidden />
        </div>
        <small class="cm-swal-upload__hint">PNG, JPG, WebP or GIF, up to 1 MB. Shown instead of the emoji.</small>
        <label>Colour<input id="cm-cat-color" type="color" value="#7c3aed" /></label>
      </div>`,
    customClass: { container: "ds-swal", popup: "ds-swal__popup", confirmButton: "ds-swal__confirm", cancelButton: "ds-swal__cancel" },
    buttonsStyling: false,
    reverseButtons: true,
    showCancelButton: true,
    confirmButtonText: "Add Category",
    focusConfirm: false,
    didOpen: () => {
      document.getElementById("cm-cat-name")?.focus();
      wireEmojiField("cm-cat-icon");
      const fileInput = document.getElementById("cm-cat-icon-file");
      const uploadButton = document.getElementById("cm-cat-icon-upload");
      const preview = document.getElementById("cm-cat-icon-preview");
      const removeButton = document.getElementById("cm-cat-icon-remove");
      const showImage = (dataUrl) => {
        iconImage = dataUrl;
        preview.src = dataUrl;
        preview.hidden = !dataUrl;
        removeButton.hidden = !dataUrl;
        uploadButton.textContent = dataUrl ? "Change image" : "Upload image";
      };
      uploadButton.addEventListener("click", () => fileInput.click());
      removeButton.addEventListener("click", () => {
        fileInput.value = "";
        showImage("");
      });
      fileInput.addEventListener("change", async () => {
        const file = fileInput.files?.[0];
        fileInput.value = "";
        if (!file) return;
        if (!ICON_IMAGE_TYPES.includes(file.type)) {
          Swal.showValidationMessage("Pick a PNG, JPG, WebP or GIF image");
          return;
        }
        if (file.size > MAX_ICON_IMAGE_BYTES) {
          Swal.showValidationMessage("The image must be 1 MB or smaller");
          return;
        }
        try {
          showImage(await readAsDataUrl(file));
          Swal.resetValidationMessage();
        } catch {
          Swal.showValidationMessage("Couldn't read that image. Please pick another one.");
        }
      });
    },
    preConfirm: () => {
      const name = document.getElementById("cm-cat-name").value.trim();
      if (!name) {
        Swal.showValidationMessage("Enter a category name");
        return false;
      }
      return {
        name,
        icon: document.getElementById("cm-cat-icon").value.trim() || "📚",
        color: document.getElementById("cm-cat-color").value,
        ...(iconImage ? { iconImage } : {}),
      };
    },
  });
  if (!value) return null;
  if (value.iconImage) {
    // The image goes to storage with the category: show that something is happening meanwhile.
    Swal.fire({
      title: "Adding category…",
      text: "Uploading the icon image.",
      customClass: { container: "ds-swal", popup: "ds-swal__popup" },
      allowOutsideClick: false,
      allowEscapeKey: false,
      didOpen: () => Swal.showLoading(),
    });
  }
  const deletedLearningZone = categories
    ? LEARNING_ZONE_CATEGORIES.find((lz) => lower(lz.label) === lower(value.name) && !categories.some((c) => lower(c.name) === lower(lz.name)))
    : null;
  if (deletedLearningZone) value.name = deletedLearningZone.name;
  try {
    const res = await api.post("/api/v2/lms/admin/courses/categories", value);
    resultDialog("success", "Category added", `"${categoryLabel(res.data?.data?.name || value.name)}" is ready for new courses.`);
    return res.data?.data || value;
  } catch (err) {
    resultDialog("error", "Could not add the category", err?.response?.data?.error || "Please try again.");
    return null;
  }
}

const escapeHtml = (value) =>
  String(value || "").replace(/[&"<>]/g, (c) => ({ "&": "&amp;", '"': "&quot;", "<": "&lt;", ">": "&gt;" }[c]));

// "+ Add Subcategory": a new subcategory of `category`, directly under it or under one of its
// subcategories (`initialParent` pre-selects which: the subcategory picked in the sidebar).
// It shows in the student Learning Zone too.
export async function promptAddSidebarSubcategory(category, subcategoryNodes = [], initialParent = "") {
  const choices = parentChoices(subcategoryNodes);
  const underOptions =
    `<option value="">${escapeHtml(categoryLabel(category))}</option>` +
    choices
      .map(({ node, depth }) => `<option value="${escapeHtml(node.key)}">${"— ".repeat(depth - 1)}${escapeHtml(`${node.icon || ""} ${node.label}`.trim())}</option>`)
      .join("");
  const { value } = await Swal.fire({
    title: `Add Subcategory to ${categoryLabel(category)}`,
    html: `
      <div class="cm-swal-form">
        <label>Subcategory name<input id="cm-new-sub-name" class="swal2-input" maxlength="60" placeholder="e.g. Poem" /></label>
        <label>Under<select id="cm-new-sub-parent" class="swal2-select">${underOptions}</select></label>
        ${emojiFieldHtml("cm-new-sub-icon")}
      </div>`,
    customClass: { container: "ds-swal", popup: "ds-swal__popup", confirmButton: "ds-swal__confirm", cancelButton: "ds-swal__cancel" },
    buttonsStyling: false,
    reverseButtons: true,
    showCancelButton: true,
    confirmButtonText: "Add Subcategory",
    focusConfirm: false,
    didOpen: () => {
      if (choices.some(({ node }) => node.key === initialParent)) document.getElementById("cm-new-sub-parent").value = initialParent;
      document.getElementById("cm-new-sub-name")?.focus();
      wireEmojiField("cm-new-sub-icon");
    },
    preConfirm: () => {
      const name = document.getElementById("cm-new-sub-name").value.trim().replace(/\s+/g, " ");
      const parent = document.getElementById("cm-new-sub-parent").value;
      if (!name) {
        Swal.showValidationMessage("Enter a subcategory name");
        return false;
      }
      const siblings = parent ? findSubcategoryNode(subcategoryNodes, parent)?.children || [] : subcategoryNodes;
      if (siblings.some((node) => lower(node.label) === lower(name))) {
        Swal.showValidationMessage(`"${name}" is already there`);
        return false;
      }
      return { name, parent, icon: document.getElementById("cm-new-sub-icon").value.trim() || "📚" };
    },
  });
  if (!value) return null;
  try {
    const res = await api.post("/api/v2/lms/admin/courses/subcategories", { ...value, category });
    const where = [categoryLabel(category), subcategoryPath(subcategoryNodes, value.parent)].filter(Boolean).join(" › ");
    resultDialog("success", "Subcategory added", `"${value.name}" was added to ${where}.`);
    return res.data?.data || value;
  } catch (err) {
    resultDialog("error", "Could not add the subcategory", err?.response?.data?.error || "Please try again.");
    return null;
  }
}

// Whether admins can delete the picked row: any subcategory (`node`) or, with none picked, any category.
// The student Learning Zone's own ones are code, so deleting one hides it (backend hiddenCourseRows.js).
export const canDeleteRow = (category, node) => Boolean(node) || Boolean(category);

// How many subcategories are in `nodes`, all levels down.
export const countSubcategories = (nodes) => nodes.reduce((n, node) => n + 1 + countSubcategories(node.children), 0);

// Whether a new subcategory still fits under `key` ("" = the category itself), see MAX_SUBCATEGORY_DEPTH.
export const canAddSubcategoryUnder = (nodes, key) => subcategoryChain(nodes, key || "").length < MAX_SUBCATEGORY_DEPTH;

// "+ Add Subcategory" (a new level under the picked category or subcategory), on the page beside the
// sidebar; nothing when `onAddSubcategory` is left out (no level fits there). Content is added by
// picking a last subcategory, which opens Add New Content.
export function SubcategoryActions({ label, onAddSubcategory }) {
  if (!onAddSubcategory) return null;
  return (
    <div className="cm-subactions" role="group" aria-label={`Add to ${label}`}>
      <button type="button" className="cm-btn cm-btn--soft cm-btn--sm" aria-label={`Add subcategory to ${label}`} title={`A new subcategory under ${label}`} onClick={onAddSubcategory}>
        <FolderPlus size={15} /> Add Subcategory
      </button>
    </div>
  );
}

// "English › Action Songs › Vocal Songs › testing": where the picked row is, as the heading of the
// page beside the sidebar. Each level above it is a link that picks that level (onPick(key), "" =
// the category itself).
export function SubcategoryCrumbs({ category, icon, chain, onPick }) {
  const levels = [{ key: "", label: categoryLabel(category), icon }, ...chain];
  return (
    <h2 className="cm-crumbs">
      {levels.map((level, i) => (
        <React.Fragment key={level.key || "category"}>
          {i > 0 && <ChevronRight size={15} className="cm-crumbs__sep" aria-hidden="true" />}
          {i === levels.length - 1 ? (
            <span className="cm-crumbs__here" aria-current="location">
              <span aria-hidden="true">{level.icon}</span>
              {level.label}
            </span>
          ) : (
            <button type="button" className="cm-crumbs__link" aria-label={`Go to ${level.label}`} onClick={() => onPick(level.key)}>
              <span aria-hidden="true">{level.icon}</span>
              {level.label}
            </button>
          )}
        </React.Fragment>
      ))}
    </h2>
  );
}

// One level of a category's subcategory tree: just the rows, each indented under its parent and
// joined to it by guide lines. A row with rows under it has an arrow to open or close them; its name
// picks it (and opens it). The rows on the way down to the picked one (`trail`) are marked.
function SubcategoryTree({ nodes, depth, tree }) {
  const { category, activeKey, trail, openNodes, toggleNode, pickNode } = tree;
  return (
    <ul className={`cm-tree${depth ? " cm-tree--nested" : ""}`}>
      {nodes.map((node) => {
        const id = `${category}::${node.key}`;
        const hasChildren = node.children.length > 0;
        const isOpen = hasChildren && openNodes.has(id);
        const isActive = activeKey === node.key;
        return (
          <li key={node.key} className={trail.has(node.key) ? "is-trail" : undefined}>
            <div className={`cm-tree__row${isActive ? " is-active" : ""}`}>
              <button
                type="button"
                className={`cm-sidebar__sub${isActive ? " is-active" : ""}`}
                onClick={() => pickNode(node, id)}
                title={node.label}
                aria-current={isActive ? "true" : undefined}
              >
                <span className="cm-sidebar__sub-icon" aria-hidden="true">{node.icon}</span>
                <span className="cm-sidebar__sub-label">{node.label}</span>
              </button>
              {hasChildren && (
                <button
                  type="button"
                  className={`cm-tree__toggle${isOpen ? " is-open" : ""}`}
                  aria-label={`${isOpen ? "Collapse" : "Expand"} ${node.label}`}
                  aria-expanded={isOpen}
                  onClick={() => toggleNode(id)}
                >
                  <ChevronRight size={14} />
                </button>
              )}
            </div>
            {isOpen && <SubcategoryTree nodes={node.children} depth={depth + 1} tree={tree} />}
          </li>
        );
      })}
    </ul>
  );
}

// Left "Course Categories" panel: just the categories and, under each, its subcategories (the same
// rows the student Learning Zone shows under it, `subcategoryNodes`, see useCourseSubcategories), as
// a tree that opens one level at a time (English > Action Songs, Scenes, Poem), plus "+ Add Category".
// Lower levels start closed, except the way to the picked subcategory. Picking a subcategory calls
// onSelectSubcategory(category, node), or else just selects the category. Picking the category while
// one of its subcategories is picked calls onSelect(category) again (back to the whole category).
// Adding to the picked row is on the page beside it (SubcategoryActions).
export function CategorySidebar({
  categories,
  selected,
  onSelect,
  onAddCategory,
  subcategoryNodes = () => [],
  selectedSubcategory = "",
  onSelectSubcategory,
}) {
  const [expanded, setExpanded] = useState(selected || "");
  // The picked row on pages that don't keep one themselves.
  const [picked, setPicked] = useState("");
  // Open subcategory rows ("<category>::<key>").
  const [openNodes, setOpenNodes] = useState(() => new Set());
  const activeKey = onSelectSubcategory ? selectedSubcategory : picked;

  useEffect(() => { if (selected) setExpanded(selected); }, [selected]);
  // Open the way down to the picked subcategory (and the subcategory itself).
  useEffect(() => {
    if (!selected || !activeKey) return;
    const ids = subcategoryChain(subcategoryNodes(selected), activeKey)
      .filter((node) => node.children.length)
      .map((node) => `${selected}::${node.key}`);
    setOpenNodes((open) => (ids.every((id) => open.has(id)) ? open : new Set([...open, ...ids])));
  }, [selected, activeKey, subcategoryNodes]);

  const toggleNode = (id) =>
    setOpenNodes((open) => {
      const next = new Set(open);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <aside className="cm-sidebar">
      <h3>Course Categories</h3>
      <ul>
        {categories.map((c) => {
          const isActive = selected === c.name;
          const nodes = subcategoryNodes(c.name);
          const isOpen = expanded === c.name && nodes.length > 0;
          const pickNode = (node, id) => {
            if (node.children.length) setOpenNodes((open) => (open.has(id) ? open : new Set([...open, id])));
            if (onSelectSubcategory) {
              onSelectSubcategory(c.name, node);
              return;
            }
            setPicked(isActive && picked === node.key ? "" : node.key);
            onSelect(c.name);
          };
          return (
            <li key={c._id || c.name}>
              <div className={`cm-sidebar__row${isActive ? " is-active" : ""}`}>
                <button
                  type="button"
                  className="cm-sidebar__cat"
                  title={c.label || c.name}
                  onClick={() => {
                    // From one of its subcategories, the category goes back to its own page (tree stays open).
                    if (isActive && activeKey) {
                      setPicked("");
                      onSelect(c.name);
                      setExpanded(c.name);
                      return;
                    }
                    onSelect(isActive ? "" : c.name);
                    setExpanded(isActive && isOpen ? "" : c.name);
                  }}
                >
                  <span className="cm-sidebar__icon" aria-hidden="true">{categoryIcon(c)}</span>
                  <span className="cm-sidebar__name">{c.label || c.name}</span>
                </button>
                {nodes.length > 0 && (
                  <button
                    type="button"
                    className="cm-sidebar__toggle"
                    aria-expanded={isOpen}
                    aria-label={`${isOpen ? "Hide" : "Show"} ${c.label || c.name} subcategories`}
                    onClick={() => setExpanded(isOpen ? "" : c.name)}
                  >
                    <ChevronDown size={16} className={isOpen ? "is-open" : ""} />
                  </button>
                )}
              </div>
              {isOpen && (
                <div className="cm-sidebar__subs">
                  <SubcategoryTree
                    nodes={nodes}
                    depth={0}
                    tree={{
                      category: c.name,
                      activeKey: isActive ? activeKey : "",
                      trail: new Set(isActive && activeKey ? subcategoryChain(nodes, activeKey).map((node) => node.key) : []),
                      openNodes,
                      toggleNode,
                      pickNode,
                    }}
                  />
                </div>
              )}
            </li>
          );
        })}
      </ul>
      {onAddCategory && (
        <button type="button" className="cm-sidebar__add" onClick={onAddCategory}>
          <Plus size={16} /> Add Category
        </button>
      )}
    </aside>
  );
}
